import { PointerEvent, useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { NavItem } from './nav-item'
import {
  NavigationProps,
  NavItemProps,
  NavItemWithChildrenProps,
} from './navigation.types'
import { useNavigationPreferences } from '../../util'

export const NAVIGATION_WIDTH = '240px'
export const NAVIGATION_COLLAPSED_WIDTH = '68px'
const HOVER_OPEN_DELAY_MS = 150
const HOVER_CLOSE_DELAY_MS = 100

const StyledNavigation = styled.nav<{
  $isCollapsed: boolean
  $isHoverExpanded: boolean
}>`
  ${({ theme, $isCollapsed, $isHoverExpanded }) => css`
    width: ${$isCollapsed ? NAVIGATION_COLLAPSED_WIDTH : NAVIGATION_WIDTH};
    transition: width 200ms ease-out;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.sizes.s};
    background: white;
    outline: 0;
    z-index: ${theme.zIndex.navigation};
    position: fixed;
    overflow-y: auto;
    top: 0;
    box-shadow: 0px 2px 16px rgba(5, 0, 56, 0.08);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    ${$isHoverExpanded &&
    css`
      overflow-x: hidden;
      white-space: nowrap;
    `}
  `}
`

// When collapsible, the inline padding centres the logo mark on the nav icons' line, collapsed or not.
const LogoSection = styled.div<{ $isCollapsible: boolean }>`
  ${({ theme, $isCollapsible }) => css`
    padding: ${theme.sizes.m}
      ${$isCollapsible
        ? `calc((${NAVIGATION_COLLAPSED_WIDTH} - ${theme.sizes.l}) / 2)`
        : theme.sizes.m};
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 500;
    font-size: 14px;
    justify-content: flex-start;
  `}
`

// One height in both states so the logo matches the collapsed mark and doesn't jump on hover.
const ShrinkableLogo = styled.div`
  ${({ theme }) => css`
    display: flex;
    min-width: 0;

    svg {
      max-width: 100%;
      width: auto;
      height: ${theme.sizes.l};
    }
  `}
`

const LogoButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    cursor: pointer;
    border-radius: ${theme.borderRadii.medium};

    &:focus-visible {
      outline: 2px solid ${theme.colors.primaryFocus};
      outline-offset: 2px;
    }
  `}
`

const MainArea = styled.div`
  ${({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: ${theme.sizes.s};
    gap: ${theme.sizes.xxs};
    overflow-y: auto;
  `}
`

const StyledNavLink = styled(NavLink)`
  ${({ theme }) => css`
    text-decoration: none;

    &:focus {
      outline: none;
    }
  `}
`

const SubItemContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding-left: ${theme.sizes.l};
  `}
`

const Footer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sizes.s};
    padding: ${theme.sizes.l};
    border-top: 2px solid ${theme.colors.gray90};
  `}
`

function isNestedItem(
  item: NavItemProps | NavItemWithChildrenProps
): item is NavItemWithChildrenProps {
  return 'children' in item
}

export const Navigation = ({
  logo,
  collapsedLogo = logo,
  items = [],
  secondaryItems,
  className,
  storageKey,
  storage,
  onItemClick,
  headerContent,
  collapsedHeaderContent,
  children,
  isCollapsed = false,
  onToggleCollapsed,
  collapseLabel = 'Collapse navigation',
  expandLabel = 'Expand navigation',
}: NavigationProps) => {
  const { toggleSection, isSectionExpanded } = useNavigationPreferences({
    storageKey,
    storage,
  })
  const [isHovered, setIsHovered] = useState(false)
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>()
  const isNarrow = isCollapsed && !isHovered
  const visibleLogo = isNarrow ? collapsedLogo : logo

  useEffect(() => () => clearTimeout(hoverTimeout.current), [])

  // Touch has no hover: a tap would flash the panel open under the finger.
  const handleHover = (isEntering: boolean) => (event: PointerEvent) => {
    if (event.pointerType === 'touch') {
      return
    }

    clearTimeout(hoverTimeout.current)
    hoverTimeout.current = setTimeout(
      () => setIsHovered(isEntering),
      isEntering ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS
    )
  }

  const handleToggleCollapsed = () => {
    // Otherwise collapsing with the pointer still over the panel would keep it hover-expanded.
    clearTimeout(hoverTimeout.current)
    setIsHovered(false)
    onToggleCollapsed?.()
  }

  return (
    <StyledNavigation
      className={className}
      $isCollapsed={isNarrow}
      $isHoverExpanded={isCollapsed && isHovered}
      onPointerEnter={handleHover(true)}
      onPointerLeave={handleHover(false)}
    >
      <LogoSection $isCollapsible={Boolean(onToggleCollapsed)}>
        {onToggleCollapsed ? (
          <LogoButton
            type="button"
            aria-label={isCollapsed ? expandLabel : collapseLabel}
            title={isCollapsed ? expandLabel : collapseLabel}
            aria-expanded={!isCollapsed}
            onClick={handleToggleCollapsed}
          >
            <ShrinkableLogo>{visibleLogo}</ShrinkableLogo>
          </LogoButton>
        ) : (
          visibleLogo
        )}
      </LogoSection>

      <MainArea>
        {isNarrow ? collapsedHeaderContent : headerContent}

        {items.map((item) => {
          if (isNestedItem(item)) {
            const isExpanded = isSectionExpanded(item.label)
            return (
              <div key={`section-${item.label}`}>
                <StyledNavLink
                  key={item.label}
                  to={item.href}
                  reloadDocument={item.reloadDocument}
                  aria-label={isNarrow ? item.label : undefined}
                  title={isNarrow ? item.label : undefined}
                  onClick={() =>
                    onItemClick?.({ label: item.label, href: item.href })
                  }
                >
                  <NavItem
                    icon={item.icon}
                    label={item.label}
                    hasChevron
                    onClick={
                      isNarrow ? undefined : () => toggleSection(item.label)
                    }
                    isActive={item.isActive}
                    isExpanded={isExpanded}
                    badge={item.badge}
                    isCollapsed={isNarrow}
                  />
                </StyledNavLink>

                {isExpanded && !isNarrow ? (
                  <SubItemContainer>
                    {item.children.map((child) => (
                      <StyledNavLink
                        key={child.label}
                        to={child.href}
                        reloadDocument={child.reloadDocument}
                        onClick={() =>
                          onItemClick?.({
                            label: child.label,
                            href: child.href,
                          })
                        }
                      >
                        <NavItem
                          label={child.label}
                          isActive={child.isActive}
                          isSubItem
                          badge={child.badge}
                        />
                      </StyledNavLink>
                    ))}
                  </SubItemContainer>
                ) : null}
              </div>
            )
          }

          return (
            <StyledNavLink
              to={item.href}
              key={item.label}
              reloadDocument={item.reloadDocument}
              aria-label={isNarrow ? item.label : undefined}
              title={isNarrow ? item.label : undefined}
              onClick={() =>
                onItemClick?.({ label: item.label, href: item.href })
              }
            >
              <NavItem
                icon={item.icon}
                label={item.label}
                isActive={item.isActive}
                badge={item.badge}
                isCollapsed={isNarrow}
              />
            </StyledNavLink>
          )
        })}

        {children}
      </MainArea>

      {secondaryItems && !isNarrow && (
        <Footer>
          {secondaryItems &&
            secondaryItems.map((item) => (
              <StyledNavLink
                key={item.label}
                to={item.href}
                reloadDocument={item.reloadDocument}
                onClick={() =>
                  onItemClick?.({ label: item.label, href: item.href })
                }
              >
                <NavItem label={item.label} isActive={item.isActive} />
              </StyledNavLink>
            ))}
        </Footer>
      )}
    </StyledNavigation>
  )
}
